import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import type { DragEndEvent } from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Link } from "@inertiajs/react"
import { GripVertical } from "lucide-react"
import { useState } from "react"

import { formatDashDate } from "@/lib/date-utils"
import { objectPositionStyle } from "@/lib/utils"

interface SubjectSummary {
  id: number
  subject_type: string | null
  display_name: string
  sex: string | null
  race: string | null
  date_of_birth: string | null
  address: string | null
  address2: string | null
  city: string | null
  state: string | null
  zip: string | null
  thumb_url: string | null
  focal_point_x?: number | null
  focal_point_y?: number | null
  has_photo: boolean
}

interface SortableSubjectsProps {
  caseId: number
  subjects: SubjectSummary[]
}

function SortableRow({
  subject,
  caseId,
}: {
  subject: SubjectSummary
  caseId: number
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: subject.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const dob = subject.date_of_birth
    ? formatDashDate(subject.date_of_birth)
    : null
  const ethnicityGender =
    [
      subject.race,
      subject.sex === "M" ? "Male" : subject.sex === "F" ? "Female" : null,
    ]
      .filter(Boolean)
      .join(", ") || null

  const street = [subject.address, subject.address2].filter(Boolean).join(", ")
  const csz = [
    subject.city,
    [subject.state, subject.zip].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border-hpi-light-gray/50 hover:bg-hpi-pale-gray flex items-start gap-3 border-b bg-white px-4 py-3 transition-colors last:border-b-0"
    >
      <button
        className="text-hpi-med-gray hover:text-hpi-dark-gray mt-1 shrink-0 cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <Link
        href={`/cases/${caseId}/subjects/${subject.id}`}
        className="flex min-w-0 flex-1 items-start gap-3"
      >
        <div className="text-hpi-dark-gray max-w-[80px] min-w-0 flex-1">
          <span className="text-xs font-medium">
            {subject.subject_type ?? "Subject"}
          </span>
        </div>

        <div className="max-w-[72px] min-w-0 flex-1">
          {subject.thumb_url ? (
            <img
              src={subject.thumb_url}
              alt={subject.display_name}
              className="h-[56px] w-[56px] rounded object-cover"
              style={{ objectPosition: objectPositionStyle(subject) }}
            />
          ) : (
            <div
              className="bg-hpi-light-gray flex h-[56px] w-[56px] items-center justify-center rounded"
              title="Photo Not Available"
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                className="text-hpi-med-gray"
              >
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <circle
                  cx="8.5"
                  cy="8.5"
                  r="1.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 17l3.5-4.5 2.5 3L15.5 11 19 17H6z"
                  fill="currentColor"
                  opacity="0.3"
                />
              </svg>
            </div>
          )}
        </div>

        <div className="text-hpi-dark-gray min-w-0 flex-1 text-sm">
          <div className="text-xs font-semibold">{subject.display_name}</div>
          <div className="text-hpi-med-gray text-xs">
            {[dob, ethnicityGender].filter(Boolean).join(" / ")}
          </div>
        </div>

        <div className="text-hpi-dark-gray min-w-0 flex-1 text-sm">
          {!street && !csz ? (
            <span className="text-hpi-med-gray">Unknown</span>
          ) : (
            <>
              {street && <div className="text-xs">{street}</div>}
              {csz && <div className="text-xs">{csz}</div>}
            </>
          )}
        </div>
      </Link>
    </div>
  )
}

export function SortableSubjects({ caseId, subjects }: SortableSubjectsProps) {
  const [items, setItems] = useState(subjects)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setItems((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id)
      const newIndex = prev.findIndex((s) => s.id === over.id)
      const reordered = arrayMove(prev, oldIndex, newIndex)

      void fetch(`/cases/${caseId}/subjects/sort`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content ?? "",
        },
        body: JSON.stringify({ ordered_ids: reordered.map((s) => s.id) }),
      })

      return reordered
    })
  }

  return (
    <>
      {/* Column headers — matches HpiDataTable header row */}
      <div className="border-hpi-light-gray border-b bg-white px-4 py-2">
        <div className="flex items-start gap-3">
          {/* Spacer for drag handle */}
          <div className="w-4 shrink-0" />
          <div className="text-hpi-active-blue max-w-[80px] min-w-0 flex-1 text-xs font-medium">
            Type
          </div>
          <div className="max-w-[72px] min-w-0 flex-1" />
          <div className="min-w-0 flex-1">
            <div className="text-hpi-active-blue text-xs font-medium">Name</div>
            <div className="text-hpi-active-blue text-xs font-medium">
              DOB / Ethnicity, Gender
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-hpi-active-blue text-xs font-medium">
              Street Address
            </div>
            <div className="text-hpi-active-blue text-xs font-medium">
              City, State, ZIP
            </div>
          </div>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((subject) => (
            <SortableRow key={subject.id} subject={subject} caseId={caseId} />
          ))}
        </SortableContext>
      </DndContext>
    </>
  )
}
